import React, { useEffect, useState } from 'react';
import { message, Row } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { ADMIN_UPDATE_USER, GET_ALL_COHORT, GET_ALL_USERS } from '../../../graphql';
import Table from './table/Table';
import Filter from './filter/FIlter';
import DropdownBtn from './dropdownBtn/Dropdown';
import { applyFilter } from '../../../util/common';

const UserHome = () => {
  const {
    loading: userLoading, data: userPayload, error: userError, refetch,
  } = useQuery(GET_ALL_USERS);
  const { loading: cohortLoading, data: cohortPayload } = useQuery(GET_ALL_COHORT);
  const [updateUser] = useMutation(ADMIN_UPDATE_USER);
  const [users, setUsers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [filterValue, setFilterValue] = useState({ cohortId: null, accountType: null, search: null });

  /**
   * A soft delete: the account is marked DELETED rather than removed, and
   * status lives on the administrator-only mutation.
   */
  const handleDeleteUser = async (id) => {
    try {
      await updateUser({
        variables: { userId: id, input: { status: 'DELETED' } },
        update: (cache) => {
          const existing = cache.readQuery({ query: GET_ALL_USERS });
          if (!existing) return;
          cache.writeQuery({
            query: GET_ALL_USERS,
            data: {
              Users: {
                ...existing.Users,
                users: existing.Users.users.filter(({ id: userId }) => userId !== id),
              },
            },
          });
        },
      });
      message.success('User deleted successfully');
    } catch (error) {
      message.error(error.message || 'Error, could not delete user');
    }
  };

  useEffect(() => {
    if (!userLoading && userPayload) {
      const { users: allUsers } = userPayload.Users;
      const filterDefault = applyFilter(allUsers, { cohortId: null, accountType: 'STUDENT' });
      setFilterValue({ ...filterValue, accountType: 'STUDENT' });
      const userRev = [...filterDefault].reverse();
      setUsers(userRev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading, userPayload]);

  useEffect(() => {
    if (!cohortLoading && cohortPayload) {
      setCohorts(cohortPayload.cohorts);
    }
  }, [cohortLoading, cohortPayload]);

  useEffect(() => {
    if (userError) message.error('Error, could not fetch users');
  }, [userError]);

  useEffect(() => {
    refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltering = (actionType, value) => {
    if (!userPayload?.Users) return;
    const { users: Users } = userPayload.Users;
    switch (actionType) {
      case 'ACCOUNT_TYPE': {
        if (value === 'ALL') {
          setFilterValue({ ...filterValue, accountType: null });
          const matches = applyFilter(Users, { ...filterValue, accountType: null });
          return setUsers(matches);
        }
        setFilterValue({ ...filterValue, accountType: value });
        const filtered = applyFilter(Users, { ...filterValue, accountType: value });
        return setUsers(filtered);
      }
      case 'SEARCH': {
        if (value === '') {
          setFilterValue({ ...filterValue, search: null });
          const match = applyFilter(Users, { ...filterValue, search: null });
          return setUsers(match);
        }
        setFilterValue({ ...filterValue, search: value });
        const filtered = [...users].filter(({ firstName, lastName, userName }) => {
          const name = `${firstName} ${lastName}`.toLocaleLowerCase();
          const valueFormatted = value.toLocaleLowerCase();
          const usernameFormatted = userName.toLocaleLowerCase();
          return name.includes(valueFormatted) || usernameFormatted.includes(valueFormatted);
        });
        return setUsers(filtered);
      }
      case 'COHORT_NAME': {
        if (value === 'ALL') {
          setFilterValue({ ...filterValue, cohortId: null });
          const reset = applyFilter(Users, { ...filterValue, cohortId: null });
          return setUsers(reset);
        }
        setFilterValue({ ...filterValue, cohortId: value });
        const match = applyFilter(Users, { ...filterValue, cohortId: value });
        return setUsers(match);
      }
      default: {
        return setUsers(Users);
      }
    }
  };

  return (
    <div>
      <Row justify="end">
        <DropdownBtn />
      </Row>
      <Filter onFilterChange={handleFiltering} cohorts={cohorts} />
      <Table handleDeleteUser={handleDeleteUser} data={users} loading={userLoading} />
    </div>
  );
};
export default UserHome;
